<template>
  <q-dialog ref="dialogRef" persistent @hide="onDialogHide">
    <q-card class="pos-dialog pos-dialog--sm column no-wrap">
      <q-card-section :class="headerClass">
        <div class="row items-center justify-between">
          <div>
            <div class="text-h6 text-white text-weight-light">{{ title }}</div>
          </div>
          <q-btn icon="close" flat round dense color="white" @click="onCancelClick" />
        </div>
      </q-card-section>

      <q-separator />

      <q-card-section class="pos-dialog__body">
        <div
          v-if="message"
          class="text-body2"
          :class="{ 'blu-confirm-dialog__message--html': html }"
        >
          <span v-if="html" v-html="message" />
          <template v-else>{{ message }}</template>
        </div>

        <q-input
          v-if="prompt"
          v-model="promptValue"
          class="q-mt-md"
          :type="prompt.type || 'text'"
          :maxlength="prompt.maxlength"
          outlined
          dense
          autofocus
          @keyup.enter="onOkClick"
        />

        <q-option-group
          v-if="options"
          v-model="selectedOption"
          class="q-mt-md"
          :type="options.type || 'radio'"
          :options="optionItems"
        />
      </q-card-section>

      <q-card-actions align="right">
        <q-btn
          v-if="showCancel"
          flat
          no-caps
          :label="cancelLabel"
          :color="cancelColor"
          @click="onCancelClick"
        />
        <q-btn
          no-caps
          :unelevated="okUnelevated"
          :flat="okFlat"
          :label="okLabel"
          :color="okColor"
          @click="onOkClick"
        />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { useDialogPluginComponent } from 'quasar'
import { useI18n } from 'vue-i18n'

const props = defineProps({
  title: { type: String, default: '' },
  message: { type: String, default: '' },
  html: { type: Boolean, default: false },
  headerClass: { type: String, default: 'dialog-header--slate' },
  okLabel: { type: String, default: '' },
  okColor: { type: String, default: 'primary' },
  okFlat: { type: Boolean, default: false },
  okUnelevated: { type: Boolean, default: true },
  cancelLabel: { type: String, default: '' },
  cancelColor: { type: String, default: 'primary' },
  showCancel: { type: Boolean, default: true },
  prompt: { type: Object, default: null },
  options: { type: Object, default: null }
})

defineEmits([...useDialogPluginComponent.emits])

const { dialogRef, onDialogHide, onDialogOK, onDialogCancel } = useDialogPluginComponent()
const { t } = useI18n()

const promptValue = ref('')
const selectedOption = ref(null)

const okLabel = computed(() => props.okLabel || t('shared.ok'))
const cancelLabel = computed(() => props.cancelLabel || t('shared.cancel'))

const optionItems = computed(() => {
  if (!props.options?.items) return []
  return props.options.items.map((item) => ({
    label: item.label,
    value: item.value
  }))
})

watch(
  () => props.prompt,
  (prompt) => {
    promptValue.value = prompt?.model ?? ''
  },
  { immediate: true }
)

watch(
  () => props.options,
  (options) => {
    selectedOption.value = options?.model ?? null
  },
  { immediate: true }
)

function onOkClick () {
  if (props.prompt) {
    onDialogOK(promptValue.value)
    return
  }
  if (props.options) {
    onDialogOK(selectedOption.value)
    return
  }
  onDialogOK()
}

function onCancelClick () {
  onDialogCancel()
}
</script>

<style scoped lang="scss">
.blu-confirm-dialog__message--html :deep(p) {
  margin: 0 0 0.5em;

  &:last-child {
    margin-bottom: 0;
  }
}
</style>
